/**
 * landingPageSections.jsx
 *
 * Reusable display sections for every landing page version config.
 * Each component receives the `data` from useLandingPageData() plus
 * any section-specific props.
 *
 * Exports:
 *   DescriptionSection          – description text (with ReactMarkdown or plain)
 *   TopCard                     – sm=8 card: language + categories + privacy/tool rows
 *   DistributionCard            – sm=4 card: right-hand distribution panel
 *   OptionalBottomCard          – full-width card: categories / languages / provenance / model
 *   ModelPropertiesSection      – model properties block inside OptionalBottomCard
 *   ProvenanceSection           – iprHolder + originalSource block
 *   DocumentationSection        – isDocumentedBy block
 *   LanguageListsSection        – pivot/source/target/meta/output language lists
 *   CategoriesSection           – optional categories column
 */

import {Stack, Grid, Typography, Card, Paper, Box} from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import LanguageListDisplay from "../sharedLandingpageSections/LanguageListDisplay";
import AdmsIdentifierDisplay from "../sharedLandingpageSections/AdmsIdentifierDisplay";
import DistributionDisplay from "../sharedLandingpageSections/DistributionDisplay";
import ModelDistributionDisplay from "../sharedLandingpageSections/ModelDistributionDisplay";
import SoftwareDistributionDisplay from "../sharedLandingpageSections/SoftwareDistributionDisplay";
import CategoriesMandatoryDisplay from "../sharedLandingpageSections/CategoriesMandatoryDisplay";
import CategoriesOptionalDisplay from "../sharedLandingpageSections/CategoriesOptionalDisplay";
import DocumentationItemDisplay from "../sharedLandingpageSections/DocumentationItemDisplay";
import ModelPropertiesDisplay from "../sharedLandingpageSections/ModelPropertiesDisplay";
import PrivacyDisplay from "../sharedLandingpageSections/PrivacyDisplay";
import PublisherDisplay from "../sharedLandingpageSections/PublisherDisplay";
import MsLanguageResourceItemDisplay from "../sharedLandingpageSections/MsLanguageResourceItemDisplay";
import MsLanguageResourceDisplay from "../sharedLandingpageSections/MsLanguageResourceDisplay";
import {keys} from "../../config/nameSpacesKeys";
import {useTranslator} from "../../context/TranslatorContext";
import {isEmpty} from "../../config/commonFunctions";
// ─────────────────────────────────────────────────────────────────────────────
// DescriptionSection
// renderMode: "markdown" (default) always
// ─────────────────────────────────────────────────────────────────────────────

export function DescriptionSection({data, renderMode = "markdown"}) {
  const {description, admsIdentifierList, getLabel, mappings, normalizeBreaks, regex} = data;
  const {defaultLanguage} = data;

  const rawValue = description.getValue(defaultLanguage);
  const hasIdentifiers = admsIdentifierList && !admsIdentifierList.isEmpty();
  return (
    <Grid container sx={{mt: 2}}>
      <Grid item xs={12} md={hasIdentifiers ? 8 : 12}>
        <Stack sx={{ pt: 1 }} spacing={1}>
          <div style={{ paddingTop: "0.5em" }}>
            <Typography variant="body2" className="key">
              {getLabel(keys.description, "description")}
            </Typography>
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {normalizeBreaks(rawValue)}
              </ReactMarkdown>
            </div>
          </div>
        </Stack>
      </Grid>
      {admsIdentifierList && !admsIdentifierList.isEmpty() && (
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: "fit-content", }} >
            <Stack spacing={1}>
              <AdmsIdentifierDisplay defaultLanguage={defaultLanguage} mappings={mappings} admsIdentifierList={admsIdentifierList.getAdmsIdentifiersList()} />
            </Stack>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
}

export function MarkdownSection({
  field,
  defaultLanguage,
  getLabel,
  normalizeBreaks,
  labelKey,
  fallbackLabel,
}) {
  const rawValue = field?.getValue(defaultLanguage);
  if (!rawValue || isEmpty(getLabel(rawValue))) {
    return null;
  }

  return (
    <div style={{paddingTop: "0.5em"}}>
      <Typography variant="body2" className="key">
        {getLabel(labelKey, fallbackLabel)}
      </Typography>
      <div className="markdown-content">
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
          {normalizeBreaks(rawValue)}
        </ReactMarkdown>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TopCard  (sm=8)
// languageSlot – language column (differs per version / lrType)
// middleSlot   – sm=12 inner row (modalities, processing resources)
// ─────────────────────────────────────────────────────────────────────────────

export function TopCard({data, languageSlot, middleSlot, showIntendedUse = false}) {
  const {messages} = useTranslator();
  const {
    isToolService,
    keywords, mediaType, lingualityType, modelFunction,
    msfunction, languageDependent, lrType,
    anonymized, anDetails, personalDataIncluded, perDetails,
    sensitiveDataIncluded, senDetails,
    hasTechnicalOrganisationalMeasure, dataProtectionPrincipleApplied,
    intendedUse, conformsTo, generationType, finetuningHardware,
  } = data;

  return (
    <Grid item sm={8} style={{display: "flex"}}>
      <Card style={{display: "flex", justifyContent: "space-between", flexDirection: "column", width: "100%"}}>
        <Grid container spacing={4}>
          {/* Language column – caller decides what goes here */}
          {languageSlot && <Grid item sm={6}><Stack>{languageSlot}</Stack></Grid>}

          {/* Mandatory categories */}
          <Grid item sm={6}>
            <CategoriesMandatoryDisplay
              defaultLanguage={data.defaultLanguage}
              mappings={data.mappings}
              topLabel={messages.landing_page_section_categories}
              keywords={keywords}
              mediaType={mediaType}
              lingualityType={lingualityType}
              modelFunction={modelFunction}
              msfunction={msfunction}
              conformsTo={conformsTo}
              languageDependent={languageDependent}
              lrType={lrType}
              generationType={generationType}
              // v0_9_5: intendedUse is passed when showIntendedUse=true
              {...(showIntendedUse ? {intendedUse} : {})}
            />
          </Grid>

          {/* Middle full-width row */}
          {middleSlot && <Grid item sm={12}>{middleSlot}</Grid>}

          {/* Privacy – all non-toolService lrTypes */}
          {!isToolService && (
            <Grid item sm={12}>
              <PrivacyDisplay
                defaultLanguage={data.defaultLanguage}
                mappings={data.mappings}
                topLabel={messages.landing_page_section_privacy}
                personalDataIncluded={personalDataIncluded}
                perDetails={perDetails}
                sensitiveDataIncluded={sensitiveDataIncluded}
                senDetails={senDetails}
                anonymized={anonymized}
                anDetails={anDetails}
                dataProtectionPrincipleApplied={dataProtectionPrincipleApplied}
                hasTechnicalOrganisationalMeasure={hasTechnicalOrganisationalMeasure}
              />
            </Grid>
          )}
        </Grid>
      </Card>
    </Grid>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DistributionCard  (sm=4)
// Picks the right display component based on lrType flags.
// In v0_9_5: corpus/lcr → DistributionDisplay, mlModel → ModelDistributionDisplay
// In default: !toolService → DistributionDisplay, toolService → SoftwareDistributionDisplay
// We pass the desired variant via `variant` prop.
// variant: "distribution" | "modelDistribution" | "softwareDistribution"
// ─────────────────────────────────────────────────────────────────────────────

export function DistributionCard({data, variant}) {
  const {distributionsList, modelDistributionsList, softwareDistributionList, getLabel} = data;
  const {defaultLanguage, mappings} = data;
  // console.log(variant);
  return (
    <Grid item sm={4} style={{display: "flex"}}>
      <Card style={{display: "flex", justifyContent: "space-between", flexDirection: "column", width: "100%"}}>
        <Stack sx={{pt: 1}} spacing={1}>
          {variant === "distribution" && (
            <DistributionDisplay
              distributionsList={distributionsList}
              defaultLanguage={defaultLanguage}
              topLabel={getLabel(keys.distribution, "distribution")}
              mappings={mappings}
            />
          )}
          {variant === "modelDistribution" && (
            <ModelDistributionDisplay
              objList={modelDistributionsList}
              defaultLanguage={defaultLanguage}
              topLabel={getLabel(keys.modelDistribution, "modelDistribution")}
              mappings={mappings}
            />
          )}
          {variant === "softwareDistribution" && (
            <SoftwareDistributionDisplay
              objList={softwareDistributionList}
              defaultLanguage={defaultLanguage}
              topLabel={getLabel(keys.distribution, "softwareDistribution")}
              mappings={mappings}
            />
          )}
        </Stack>
      </Card>
    </Grid>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CategoriesSection
// ─────────────────────────────────────────────────────────────────────────────

export function CategoriesSection({data}) {
  const {messages} = useTranslator();
  const {
    domain, spatial, temporal, languageType, multilingualityType,
    lcrSubclass, corpusSubclass, annotationType, linguisticInformation,
    columnCount, defaultLanguage, mappings,
  } = data;

  return (
    <Grid item sm={12 / columnCount}>
      <Typography variant="h4" noWrap className="section--title">
        {messages.landing_page_section_categories_optional}
      </Typography>
      <CategoriesOptionalDisplay
        topLabel={messages.landing_page_section_categories_optional}
        defaultLanguage={defaultLanguage}
        mappings={mappings}
        domain={domain} spatial={spatial} temporal={temporal}
        multilingualityType={multilingualityType}
        lcrSubclass={lcrSubclass} corpusSubclass={corpusSubclass}
        annotationType={annotationType} linguisticInformation={linguisticInformation}
        languageType={languageType} />
    </Grid>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LanguageListsSection
// showOutputLanguage: true for v0_9_5 (ML has outputLanguage, others don't)
// ─────────────────────────────────────────────────────────────────────────────

export function LanguageListsSection({data, showOutputLanguage = false}) {
  const {messages} = useTranslator();
  const {
    pivotLanguageList, sourceLanguageList, targetLanguageList,
    metalanguageList, outputLanguage,
    columnCount, getLabel, defaultLanguage, mappings,
  } = data;

  const hasAny =
    pivotLanguageList.getLanguagesList()?.length > 0 ||
    sourceLanguageList.getLanguagesList()?.length > 0 ||
    targetLanguageList.getLanguagesList()?.length > 0 ||
    metalanguageList.getLanguagesList()?.length > 0 ||
    (showOutputLanguage && outputLanguage.getLanguagesList()?.length > 0);

  return (
    <Grid item sm={12 / columnCount}>
      <Stack spacing={1}>
        {hasAny && (
          <Typography variant="h4" noWrap className="section--title">
            {messages.landing_page_section_otherLanguages}
          </Typography>
        )}
        <LanguageListDisplay languageList={pivotLanguageList.getLanguagesList()} defaultLanguage={defaultLanguage} mappings={mappings} topLabel={getLabel(keys.pivotLanguage, "pivot language")} />
        <LanguageListDisplay languageList={sourceLanguageList.getLanguagesList()} defaultLanguage={defaultLanguage} mappings={mappings} topLabel={getLabel(keys.sourceLanguage, "source language")} />
        <LanguageListDisplay languageList={targetLanguageList.getLanguagesList()} defaultLanguage={defaultLanguage} mappings={mappings} topLabel={getLabel(keys.targetLanguage, "target language")} />
        <LanguageListDisplay languageList={metalanguageList.getLanguagesList()} defaultLanguage={defaultLanguage} mappings={mappings} topLabel={getLabel(keys.metalanguage, "metalanguage")} />
        {showOutputLanguage && (
          <LanguageListDisplay languageList={outputLanguage.getLanguagesList()} defaultLanguage={defaultLanguage} mappings={mappings} topLabel={getLabel(keys.outputLanguage, "outputLanguage")} />
        )}
      </Stack>
    </Grid>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ModelPropertiesSection
// showExtendedProps: true for v0_9_5 (instructions, designSpecs, benchmarks, etc.)
// ─────────────────────────────────────────────────────────────────────────────

export function ModelPropertiesSection({data, showExtendedProps = false}) {
  const {messages} = useTranslator();
  const {
    modelType, variantOf, bias, limitations, contextLength, quantized,
    quantizationProcess, parameterPrecision, trainedOn, finetuneDataset,
    creationDetails, evaluationDataset, evaluationResults,
    // extended (v0_9_5)
    instructions, designSpecifications, requiresSoftware,
    finetuningMethodology, hasBenchmark, hasTrainingImpact, hasInferencingImpact,
    defaultLanguage, mappings, normalizeBreaks, evaluation, baseModel,
    finetuningHardware, finetuningDescription,
  } = data;

  return (
    <div style={{pb: 1}}>
      <Typography variant="h4" noWrap className="section--title">
        {messages.editor_mlModel_group_title}
      </Typography>
      <ModelPropertiesDisplay
        defaultLanguage={defaultLanguage}
        mappings={mappings}
        modelType={modelType}
        variantOf={variantOf}
        bias={bias}
        limitations={limitations}
        contextLength={contextLength}
        quantized={quantized}
        quantizationProcess={quantizationProcess}
        parameterPrecision={parameterPrecision}
        trainedOn={trainedOn}
        finetuneDataset={finetuneDataset}
        creationDetails={creationDetails}
        evaluationDataset={evaluationDataset}
        evaluationResults={evaluationResults}
        normalizeBreaks={normalizeBreaks}
        // extended props — ModelPropertiesDisplay ignores undefined props safely
        {...(showExtendedProps ? {
          instructions,
          evaluation,
          designSpecifications,
          requiresSoftware,
          finetuningMethodology,
          hasBenchmark,
          hasTrainingImpact,
          hasInferencingImpact,
          baseModel,
          finetuningHardware,
          finetuningDescription,
        } : {})}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ProvenanceSection
// ─────────────────────────────────────────────────────────────────────────────

export function ProvenanceSection({data}) {
  const {messages} = useTranslator();
  const {
    iprHolder, hasOriginalSource, originalSourceDescription,
    getLabel, defaultLanguage, mappings, generationProcess,
    seedDataset, generationModel, normalizeBreaks, creator,
  } = data;

  return (
    <>
      <Typography variant="h4" noWrap className="section--title">
        {messages.landing_page_section_provenance}
      </Typography>
      <PublisherDisplay
        defaultLanguage={defaultLanguage}
        mappings={mappings}
        topLabel={getLabel(keys.iprHolder, "IprHolder")}
        iprHolder={iprHolder.getObjList()}
      />
      <PublisherDisplay
        defaultLanguage={defaultLanguage}
        mappings={mappings}
        topLabel={getLabel(keys.dctCreator, "creator")}
        iprHolder={creator.getObjList()}
      />
      {hasOriginalSource && !hasOriginalSource.isEmpty() && (
        <DocumentationItemDisplay
          defaultLanguage={defaultLanguage}
          mappings={mappings}
          topLabel={getLabel(keys.hasOriginalSource, "hasOriginalSource")}
          documentationList={hasOriginalSource.getObjList()}
        />
      )}
      {!originalSourceDescription.isEmpty() && (
        <Stack sx={{pt: 1, pb: 2}} spacing={1}>
          <Typography variant="body2" className="key">
            {getLabel(keys.originalSourceDescription, "originalSourceDescription")}
          </Typography>
          <Typography variant="body1">
            {originalSourceDescription.getValue(defaultLanguage)}
          </Typography>
        </Stack>
      )}{" "}

      {!isEmpty(getLabel(generationProcess.getValue(defaultLanguage))) && (
        <MarkdownSection
          field={generationProcess}
          defaultLanguage={defaultLanguage}
          getLabel={getLabel}
          normalizeBreaks={normalizeBreaks}
          labelKey={keys.generationProcess}
          fallbackLabel="generationProcess"
        />
      )}

      <MsLanguageResourceItemDisplay defaultLanguage={defaultLanguage} mappings={mappings}
        topLabel={getLabel(keys.seedDataset, "seedDataset")} normalizeBreaks={normalizeBreaks}
        msLanguageResourceList={seedDataset.getObjList()} />

      <MsLanguageResourceDisplay defaultLanguage={defaultLanguage} mappings={mappings}
        topLabel={getLabel(keys.generationModel, "generationModel")}
        msLanguageResource={generationModel} normalizeBreaks={normalizeBreaks}
      />

    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DocumentationSection
// ─────────────────────────────────────────────────────────────────────────────

export function DocumentationSection({data}) {
  const {messages} = useTranslator();
  const {isDocumentedBy, getLabel, defaultLanguage, mappings} = data;

  return (
    <>
      <Typography variant="h4" noWrap className="section--title">
        {messages.landing_page_section_documentation}
      </Typography>
      {isDocumentedBy && !isDocumentedBy.isEmpty() && (
        <DocumentationItemDisplay
          defaultLanguage={defaultLanguage}
          mappings={mappings}
          topLabel={getLabel(keys.isDocumentedBy, "isDocumentedBy")}
          documentationList={isDocumentedBy.getObjList()}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OptionalBottomCard
// Full-width card containing categories / language lists / provenance+docs+model.
// The caller passes the three column slot components directly.
// ─────────────────────────────────────────────────────────────────────────────

export function OptionalBottomCard({data, categoriesSlot, languageListsSlot, rightSlot}) {
  const {hasCategoriesDisplay, hasLanguageLists, hasProvenance, hasDocumentation, hasModelProperties} = data;

  const hasAny = hasCategoriesDisplay || hasProvenance || hasDocumentation || hasModelProperties || hasLanguageLists;
  if (!hasAny) return null;

  return (
    <Grid item sm={12}>
      <Card style={{width: "100%"}}>
        <Grid container spacing={2}>
          {hasCategoriesDisplay && categoriesSlot}
          {hasLanguageLists && languageListsSlot}
          {(hasProvenance || hasDocumentation || hasModelProperties) && rightSlot}
        </Grid>
      </Card>
    </Grid>
  );
}
